import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { COLORS } from '../../constants/colorPallete';
import getPlatformType from '../../helpers/getPlatform';
import DropDownPicker from '../../commonComponents/DropDownPicker.web';
import { createArray } from '../../helpers/functionHelpers';
import { LineSaperator } from '../tds/style';
import { ODSText } from '../../../openbrix-design-system/ui/atoms/ODSText';
import { getTokens } from '../../../openbrix-design-system/design-tokens/color-tokens';
import ODSRadioGroup from '../../../openbrix-design-system/ui/atoms/ODSRadioGroup';
import { ODSIcon } from '../../../openbrix-design-system/ui/atoms/ODSIcon';
import ODSButton from '../../../openbrix-design-system/ui/atoms/ODSButton';
import CustomTextInput from '../../commonComponents/customTextInput/customTextInput';

const TenantInfoFormComponent = (props) => {
  const themeObj = getTokens('light').colors;
  const dispatch = useDispatch();
  const layoutType = getPlatformType();

  const [fetchErrors, setFetchErrors] = useState({});
  const [loadingFetch, setLoadingFetch] = useState({});

  const { initialValue: state, onSelect, level2Type } = props;

  /** ----------------------------
   *  Helpers
   * ----------------------------- */
  const updateStateAtIndex = useCallback(
    (index, newData) => {
      const updated = state?.[state?.level_1_key]?.map((obj, i) =>
        i === index ? { ...obj, ...newData } : obj
      );
      onSelect({ ...state, [state.level_1_key]: updated });
    },
    [state, onSelect]
  );

  /** ----------------------------
   *  Event Handlers
   * ----------------------------- */
  const handleMainRadioChange = useCallback(
    (value) => {
      if (value === 'yes') {
        onSelect({ ...state, value: 'Yes' });
      } else {
        onSelect({ ...state, value: 'No', [state.level_1_key]: [] });
      }
    },
    [state, onSelect]
  );

  const handleDropdownChange = useCallback(
    (e) => {
      const count = e.value;
      const newArray = Array.from({ length: count }, () => ({
        [state.level_2_key]: '',
      }));
      onSelect({ ...state, [state.level_1_key]: newArray });
    },
    [state, onSelect]
  );

  const handleUnlink = useCallback(
    (index) => {
      updateStateAtIndex(index, {
        [state.level_3_key]: '',
        tenant_data: null,
      });
    },
    [updateStateAtIndex, state]
  );

  const handleRevokeInvite = useCallback(
    (index) => {
      updateStateAtIndex(index, {
        tlyfe_id_exist: 'No',
        invite_email: '',
        invite_sent: false,
      });
    },
    [updateStateAtIndex]
  );

  const handleRadioChange = useCallback(
    (index, value) => {
      const selectedValue = value === 'yes' ? 'Yes' : 'No';
      updateStateAtIndex(index, {
        [state.level_2_key]: selectedValue,
        tenant_data: null,
        invite_email: '',
        invite_sent: false,
      });
    },
    [updateStateAtIndex, state]
  );

  const handleTenantIdChange = useCallback(
    (index, text) => {
      updateStateAtIndex(index, { [state.level_3_key]: text });
    },
    [updateStateAtIndex, state]
  );

  const handleConnectPress = useCallback(
    (index, tenantId) => {
      setLoadingFetch((prev) => ({ ...prev, [index]: true }));

      setTimeout(() => {
        const cb =
          tenantId === 'Mock'
            ? {
                success: true,
                data: {
                  first_name: 'John',
                  last_name: 'Do',
                  id: '12345678',
                  email: 'john@example.com',
                  connected: true,
                },
              }
            : { success: false };

        if (cb.success) {
          updateStateAtIndex(index, { tenant_data: cb.data });
        } else {
          setFetchErrors((prev) => ({
            ...prev,
            [index]:
              'tlyfe ID not found. Please double-check or invite via email instead.',
          }));
        }
        setLoadingFetch((prev) => ({ ...prev, [index]: false }));
      }, 1200);
    },
    [updateStateAtIndex]
  );

  const handleEmailChange = useCallback(
    (index, email) => {
      updateStateAtIndex(index, { invite_email: email });
      setFetchErrors((prev) => {
        const copy = { ...prev };
        delete copy[index];
        return copy;
      });
    },
    [updateStateAtIndex]
  );

  const handleSendInvite = useCallback(
    (index, email) => {
      if (!email) return;

      setLoadingFetch((prev) => ({ ...prev, [index]: true }));
      setFetchErrors((prev) => ({ ...prev, [index]: null }));

      setTimeout(() => {
        const isSuccess =
          email.endsWith('@yopmail.com') || email.includes('mock');

        if (isSuccess) {
          updateStateAtIndex(index, {
            invite_email: email,
            invite_sent: true,
          });
        } else {
          setFetchErrors((prev) => ({
            ...prev,
            [index]:
              'Failed to send invite. Please check the email and try again.',
          }));
        }
        setLoadingFetch((prev) => ({ ...prev, [index]: false }));
      }, 1200);
    },
    [updateStateAtIndex]
  );

  /** ----------------------------
   *  JSX Rendering
   * ----------------------------- */
  return (
    <>
      {/* Main Radio */}
      <View style={{ flexDirection: 'row' }}>
        <ODSRadioGroup
          width="100%"
          label={`${state.Label} *`}
          subLable={state.subLable}
          checked={state?.value === 'Yes' ? 'yes' : state?.value === 'No' ? 'no' : ''}
          onChange={handleMainRadioChange}
        />
      </View>

      {/* Dropdown (Level 1) */}
      {state?.level_1_key && state?.value === 'Yes' && (
        <View style={{ width: '100%', marginTop: 20 }}>
          <ODSText type={level2Type ? 'sm' : 'sm-bold'} color={themeObj.text.primary}>
            {state?.level_1_Label}
          </ODSText>

          <DropDownPicker
            id="openflo-Title"
            items={createArray(
              state?.formType === 'Children' ? 'childrenCount' : ''
            ).map((val) => ({ value: val, label: val }))}
            placeholder="Title"
            defaultValue={state?.[state?.level_1_key]?.length || ''}
            onChangeItem={handleDropdownChange}
            dropDownStyle={{ backgroundColor: 'white' }}
            zIndex={1001}
            bColor="#B2B2B2"
          />
        </View>
      )}

      {/* LEVEL 2 section */}
      {level2Type
        ? renderLevel2Type({
            state,
            themeObj,
            fetchErrors,
            loadingFetch,
            handleUnlink,
            handleRevokeInvite,
            handleRadioChange,
            handleTenantIdChange,
            handleConnectPress,
            handleEmailChange,
            handleSendInvite,
          })
        : renderBasicLevel(state, themeObj, updateStateAtIndex)}

      {/* Separator */}
      {state?.level_1_key ? (
        <LineSaperator style={{ marginVertical: 32, opacity: 0.4, backgroundColor: 'black' }} />
      ) : (
        <View style={{ marginBottom: 31 }} />
      )}
    </>
  );
};

/** ----------------------------
 *  Render Helpers
 * ----------------------------- */

const renderLevel2Type = ({
  state,
  themeObj,
  fetchErrors,
  loadingFetch,
  handleUnlink,
  handleRevokeInvite,
  handleRadioChange,
  handleTenantIdChange,
  handleConnectPress,
  handleEmailChange,
  handleSendInvite,
}) => {
  return (
    <>
      {state?.[state?.level_1_key]?.map((item, index) => {
        const hasTenantData = !!item?.tenant_data;
        const hasInvite = !!item?.invite_sent;

        return (
          <View style={{ marginTop: 20 }} key={`joint-${index}`}>
            <ODSText marginBottom={20} type="sm-bold" color={themeObj.text.primary}>
              Joint tenant {index + 1}
            </ODSText>

            {hasTenantData ? (
              <ConnectedTenant
                item={item}
                themeObj={themeObj}
                index={index}
                handleUnlink={handleUnlink}
              />
            ) : hasInvite ? (
              <InviteSent
                item={item}
                index={index}
                themeObj={themeObj}
                handleRevokeInvite={handleRevokeInvite}
              />
            ) : (
              <TenantInputFlow
                state={state}
                item={item}
                index={index}
                themeObj={themeObj}
                fetchErrors={fetchErrors}
                loadingFetch={loadingFetch}
                handleRadioChange={handleRadioChange}
                handleTenantIdChange={handleTenantIdChange}
                handleConnectPress={handleConnectPress}
                handleEmailChange={handleEmailChange}
                handleSendInvite={handleSendInvite}
              />
            )}
          </View>
        );
      })}
    </>
  );
};

/** ----------------------------
 *  Subcomponents
 * ----------------------------- */

const ConnectedTenant = ({ item, index, themeObj, handleUnlink }) => (
  <View>
    <ODSText type="sm" color={themeObj.text.primary}>
      {item.tenant_data.first_name} {item.tenant_data.last_name}
    </ODSText>
    <ODSText marginTop={4} type="sm" color={themeObj.text.secondary}>
      tlyfe ID: {item.tenant_data.id}
    </ODSText>

    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 20 }}>
      <ODSIcon icon="TB-Check-Circle" size={24} color={themeObj.feedback.success['500']} />
      <Text style={{ marginLeft: 6, color: 'green', fontSize: 14 }}>Connected</Text>
    </View>

    <TouchableOpacity style={styles.unlinkButton} onPress={() => handleUnlink(index)}>
      <ODSIcon icon="Icon-1" size={24} color={themeObj.feedback.error['500']} />
      <ODSText type="sm-bold" color={themeObj.text.secondary}>
        Unlink
      </ODSText>
    </TouchableOpacity>

    <ODSText marginTop={8} color={themeObj.text.secondary} type="sm">
     
      You have been connected as a joint tenant on xx/xx/xxxx. Click this button to remove the connection.
    </ODSText>
  </View>
);

const InviteSent = ({ item, index, themeObj, handleRevokeInvite }) => (
  <View style={styles.inviteBox}>
    <ODSText type="sm" color={themeObj.text.primary}>
      {`You invited ${item.invite_email} to join this tenancy. We’ll let you know once it’s accepted.`}
    </ODSText>
    <TouchableOpacity style={styles.revokeButton} onPress={() => handleRevokeInvite(index)}>
      <Ionicons name="close-circle-outline" size={18} color="#FF3B30" />
      <Text style={styles.revokeText}>Revoke Invite</Text>
    </TouchableOpacity>
  </View>
);

const TenantInputFlow = ({
  state,
  item,
  index,
  themeObj,
  fetchErrors,
  loadingFetch,
  handleRadioChange,
  handleTenantIdChange,
  handleConnectPress,
  handleEmailChange,
  handleSendInvite,
}) => (
  <>
    {/* Radio Group */}
    <ODSRadioGroup
      width="100%"
      label={state?.level_2_Label}
      checked={
        item[state?.level_2_key] === 'Yes'
          ? 'yes'
          : item[state?.level_2_key] === 'No'
          ? 'no'
          : ''
      }
      onChange={(v) => handleRadioChange(index, v)}
    />

    {/* YES → tlyfe ID */}
    {item[state?.level_2_key] === 'Yes' && (
      <View style={{ marginTop: 10 }}>
        <CustomTextInput
          id="level_3_Field"
          width="100%"
          title="tlyfe ID"
          type="text"
          onChangeText={(t) => handleTenantIdChange(index, t)}
          bColor="#B2B2B2"
          name="tenant_id"
          defaultValue={item[state?.level_3_key]}
        />

        {fetchErrors[index] && <Text style={styles.errorText}>{fetchErrors[index]}</Text>}

        {item[state?.level_3_key] && (
          <ODSButton
            rIcon="TB-Edit"
            restStyle={styles.connectButton}
            themeColor={themeObj.main.purple}
            disabled={loadingFetch[index]}
            onPress={() => handleConnectPress(index, item[state?.level_3_key])}
          >
            {loadingFetch[index] ? 'Connecting...' : 'Connect'}
          </ODSButton>
        )}
      </View>
    )}

    {/* NO → Email Invite */}
    {item[state?.level_2_key] === 'No' && (
      <View style={{ marginTop: 10 }}>
        <CustomTextInput
          id="invite_email"
          width="100%"
          title="Email address"
          type="text"
          onChangeText={(email) => handleEmailChange(index, email)}
          bColor="#B2B2B2"
          name="invite_email"
          defaultValue={item.invite_email || ''}
        />

        {fetchErrors[index] && <Text style={styles.errorText}>{fetchErrors[index]}</Text>}

        {item.invite_email?.includes('@') && (
          <ODSButton
            restStyle={styles.sendInviteButton}
            themeColor={themeObj.main.purple}
            disabled={!!loadingFetch[index]}
            onPress={() => handleSendInvite(index, item.invite_email?.trim())}
          >
            {loadingFetch[index] ? 'Sending...' : 'Send Invite'}
          </ODSButton>
        )}
      </View>
    )}
  </>
);

const renderBasicLevel = (state, themeObj, updateStateAtIndex) => (
  <>
    {state?.[state?.level_1_key]?.length > 0 && (
      <>
        <ODSText marginTop={23} type="sm-bold" color={themeObj.text.primary}>
          {state?.level_2_Label}
        </ODSText>

        {state[state.level_1_key].map((item, index) => (
          <View key={`joint-${index}`} style={{ flexDirection: 'row', marginTop: 8, alignItems: 'center' }}>
            <Text style={{ fontSize: 14, color: 'black', marginRight: 16 }}>{index + 1}</Text>
            <View style={{ flex: 1 }}>
              <CustomTextInput
                id="level_2_Feild"
                width="100%"
                title=""
                type="text"
                onChangeText={(val) => updateStateAtIndex(index, { [state.level_2_key]: val })}
                bColor="#B2B2B2"
                name="firstName"
                defaultValue={item[state?.level_2_key]}
              />
            </View>
          </View>
        ))}
      </>
    )}
  </>
);

/** ----------------------------
 *  Styles
 * ----------------------------- */
const styles = {
  unlinkButton: {
    marginTop: 20,
    borderWidth: 1.5,
    borderColor: '#FF3B30',
    borderRadius: 25,
    paddingVertical: 8,
    paddingHorizontal: 18,
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexDirection: 'row',
    gap: 6,
  },
  inviteBox: {
    borderWidth: 1,
    borderColor: '#E5E5E5',
    borderRadius: 10,
    padding: 16,
    backgroundColor: '#F9FAFB',
  },
  revokeButton: {
    marginTop: 15,
    borderWidth: 1.5,
    borderColor: '#FF3B30',
    borderRadius: 25,
    paddingVertical: 8,
    paddingHorizontal: 18,
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexDirection: 'row',
    gap: 6,
  },
  revokeText: { color: '#FF3B30', fontWeight: '600' },
  errorText: { color: '#D93025', marginTop: 8, fontSize: 13 },
  connectButton: { marginBottom: 10, width: 110, height: 40, alignSelf: 'flex-end' },
  sendInviteButton: { marginBottom: 10, width: 200, height: 40, alignSelf: 'flex-end' },
};

export default TenantInfoFormComponent;
